import type { PortalSiteDisplaySettingsDto } from '@supply-mover-2-0/shared';
import { useTranslation } from '@supply-mover-2-0/web-ui';
import { Fragment, type ReactElement } from 'react';

interface ProductIdentifierLinesProps {
	/**
	 * The item's customer-facing number (the DTO's `externalId`), shown as
	 * "#200715" on catalog cards. The list row and the product page already
	 * show it themselves, so only the `card` variant renders it.
	 */
	sku: string | null;
	/** NDC, already formatted 5-4-2 by the API; null when the storefront hides it. */
	ndc: string | null;
	/** Resolved Size display value; null when hidden or absent. */
	size: string | null;
	display: PortalSiteDisplaySettingsDto;
	/**
	 * - `card`   — stacked compact lines under a catalog / favorites card:
	 *   "#200715", "NDC 70756-0215-51", "Size 500TB".
	 * - `detail` — stacked "Label: value" lines matching the product page's
	 *   other fact lines (Manufacturer, Category, UOM).
	 * - `inline` — one row joined by dots for the catalog list view, e.g.
	 *   "NDC 70756-0215-51 · Size 500TB".
	 */
	variant: 'card' | 'detail' | 'inline';
	className?: string;
}

interface IdentifierLineModel {
	key: 'sku' | 'ndc' | 'size';
	label: string;
	value: string;
}

/** Non-blank value, or null. */
function presentValue(value: string | null): string | null {
	return value != null && value.trim() !== '' ? value.trim() : null;
}

/**
 * The item number, NDC and Size lines on catalog cards, list rows and the
 * product page (PD-16126). The item number always shows, as "#200715", the
 * format the list row already used. NDC and Size are opt-in per portal site:
 * each self-gates on its display flag plus a non-blank value, and the server
 * already nulls them when the storefront hides them.
 */
export function ProductIdentifierLines({
	sku,
	ndc,
	size,
	display,
	variant,
	className,
}: ProductIdentifierLinesProps): ReactElement | null {
	const { t } = useTranslation();

	const skuValue = variant === 'card' ? presentValue(sku) : null;
	const ndcValue = display.showItemNdc ? presentValue(ndc) : null;
	const sizeValue = display.showItemSize ? presentValue(size) : null;

	const lines: Array<IdentifierLineModel> = [
		...(skuValue != null
			? [{ key: 'sku' as const, label: t('#'), value: skuValue }]
			: []),
		...(ndcValue != null
			? [{ key: 'ndc' as const, label: t('NDC'), value: ndcValue }]
			: []),
		...(sizeValue != null
			? [{ key: 'size' as const, label: t('Size'), value: sizeValue }]
			: []),
	];

	if (lines.length === 0) {
		return null;
	}

	if (variant === 'inline') {
		return (
			<div
				className={`flex items-center gap-1.5 text-xs text-subtle min-w-0 ${className ?? ''}`}
			>
				{lines.map((line, index) => (
					<Fragment key={line.key}>
						{index > 0 && <span className="text-disabled">{t('·')}</span>}
						<span className="truncate">
							{line.label} {t(line.value)}
						</span>
					</Fragment>
				))}
			</div>
		);
	}

	if (variant === 'detail') {
		return (
			<div className={className}>
				{lines.map((line) => (
					<p key={line.key} className="text-sm text-brand-muted mb-1">
						{line.label}: {t(line.value)}
					</p>
				))}
			</div>
		);
	}

	return (
		<div
			className={`flex flex-col gap-0.5 text-[11px] leading-tight min-w-0 ${className ?? ''}`}
		>
			{lines.map((line) => (
				<span key={line.key} className="text-brand-muted truncate">
					{line.key === 'sku' ? (
						<>
							{line.label}
							{t(line.value)}
						</>
					) : (
						<>
							{line.label} {t(line.value)}
						</>
					)}
				</span>
			))}
		</div>
	);
}
